import AnsiToHtml from "ansi-to-html";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import * as React from "react";
import { useLocation, useParams } from "react-router-dom";
import { CloudService, IBuildDto, ICloudConsoleResponse } from "../../../../services/cloud-service";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import KuikaSmallBoldText from "../../../ui-kit/typography/SmallBoldText/SmallBoldText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./deployment.scss";

type DeploymentRow = {
  id: string;
  project: string;
  environment: string;
  lastDeploymentAt: string;
  lastBuildNumber: number;
  lastBuildResult: string;
  deploymentChannel?: string;
};

interface DeploymentProps extends IWithHistoryProp {
  appName?: string;
  buildNumber?: string;
  deploymentRow?: DeploymentRow;
  channelFromQuery?: string;
}

interface DeploymentState {
  loading: boolean;
  error?: string;
  buildData?: IBuildDto;
  selectedConsoleType: "parent" | "backend" | "frontend";
  consoleLogs: {
    parent: { loading: boolean; error?: string; output?: string; renderedHtml?: string };
    backend: { loading: boolean; error?: string; output?: string; renderedHtml?: string };
    frontend: { loading: boolean; error?: string; output?: string; renderedHtml?: string };
  };
  domain?: string;
}

class DeploymentBase extends React.Component<DeploymentProps, DeploymentState> {
  private ansiConverter: AnsiToHtml;

  constructor(props: DeploymentProps) {
    super(props);
    this.state = {
      loading: false,
      error: undefined,
      buildData: undefined,
      selectedConsoleType: "parent",
      consoleLogs: {
        parent: { loading: false, error: undefined, output: undefined },
        backend: { loading: false, error: undefined, output: undefined },
        frontend: { loading: false, error: undefined, output: undefined }
      },
      domain: undefined
    };

    this.ansiConverter = new AnsiToHtml({
      fg: "#c9d1d9",
      bg: "#0d1117",
      newline: false,
      // escapeXML:true — the deploy console output is user-influenced build output rendered via
      // dangerouslySetInnerHTML; HTML in it must be escaped to avoid stored XSS in shared projects.
      escapeXML: true,
      stream: false,
      colors: {
        0: "#484f58", // black (use gray instead of pure black for visibility)
        1: "#ff7b72", // red
        2: "#3fb950", // green
        3: "#d29922", // yellow
        4: "#58a6ff", // blue
        5: "#bc8cff", // magenta
        6: "#39c5cf", // cyan
        7: "#c9d1d9", // white
        8: "#6e7681", // bright black (brighter gray)
        9: "#ffa198", // bright red
        10: "#56d364", // bright green
        11: "#e3b341", // bright yellow
        12: "#79c0ff", // bright blue
        13: "#d2a8ff", // bright magenta
        14: "#56d4dd", // bright cyan
        15: "#f0f6fc" // bright white
      }
    });
  }

  componentDidMount() {
    const { appName, buildNumber }: { appName?: string; buildNumber?: string } = this.props;
    if (appName && buildNumber) {
      this.fetchBuildData(appName, parseInt(buildNumber));
    }
  }

  componentDidUpdate(prevProps: Readonly<DeploymentProps>, prevState: Readonly<DeploymentState>): void {
    if (!prevState.buildData && this.state.buildData) {
      this.fetchConsoleOutput("parent");
      this.fetchConsoleOutput("backend");
      this.fetchConsoleOutput("frontend");
    }
  }

  fetchBuildData = async (project: string, buildNum: number): Promise<void> => {
    this.setState({ loading: true, error: undefined });

    try {
      const response: AxiosResponse<IBuildDto[]> = await CloudService.GetBuilds(project, 1000);

      if (response && response.data) {
        const expectedChannel = this.props.deploymentRow?.deploymentChannel?.toLowerCase();
        // Find the specific build by number
        const channelFromQuery = this.props.channelFromQuery?.toLowerCase();
        let build: IBuildDto | undefined = response.data.find((b: IBuildDto) => {
          if (b.number !== buildNum || b.project !== project) return false;
          const deploymentChannel = (b.deploymentChannel ?? "").toLowerCase();
          if (expectedChannel) return deploymentChannel === expectedChannel;
          if (channelFromQuery) return deploymentChannel === channelFromQuery;
          return true;
        });

        if (!build) {
          build = response.data.find((b: IBuildDto) => b.number === buildNum && b.project === project);
        }

        if (build) {
          this.setState({ buildData: build, loading: false });
        } else {
          this.setState({
            error: `Build #${buildNum} not found for project ${project}`,
            loading: false
          });
        }
      } else {
        throw new Error("No data received from cloud service");
      }
    } catch (error: unknown) {
      console.error("Error fetching build data:", error);
      this.setState({
        error: error instanceof Error ? error.message : "Failed to fetch build data",
        loading: false
      });
    }
  };

  fetchConsoleOutput = async (consoleType: "parent" | "backend" | "frontend"): Promise<void> => {
    const { buildData }: { buildData?: IBuildDto } = this.state;
    if (!buildData) return;

    this.setState((prevState) => ({
      consoleLogs: {
        ...prevState.consoleLogs,
        [consoleType]: { loading: true, error: undefined, output: undefined }
      }
    }));

    try {
      const project: string = buildData.project;
      const buildNum: number = buildData.number;
      const deploymentChannel =
        buildData.deploymentChannel || this.props.deploymentRow?.deploymentChannel || this.props.channelFromQuery;

      const response: AxiosResponse<ICloudConsoleResponse> = await CloudService.GetConsoleOutput(
        consoleType,
        buildNum,
        project,
        deploymentChannel
      );

      if (response && response.data) {
        const output: string = response.data.consoleOutput;
        const renderedHtml: string | undefined = output ? this.ansiConverter.toHtml(output) : undefined;

        let parsedDomain: string | undefined;
        if (consoleType === "parent" && output) {
          parsedDomain = this.extractFrontendDomain(output);
        }

        this.setState((prevState: Readonly<DeploymentState>) => ({
          consoleLogs: {
            ...prevState.consoleLogs,
            [consoleType]: { loading: false, error: undefined, output, renderedHtml }
          },
          domain: parsedDomain || prevState.domain
        }));
      } else {
        throw new Error("No console output received");
      }
    } catch (error: unknown) {
      console.error(`Error fetching ${consoleType} console output:`, error);
      this.setState((prevState: Readonly<DeploymentState>) => ({
        consoleLogs: {
          ...prevState.consoleLogs,
          [consoleType]: {
            loading: false,
            error: error instanceof Error ? error.message : "Failed to fetch console output",
            output: undefined
          }
        }
      }));
    }
  };

  private stripAnsi = (value: string): string => {
    try {
      return value.replace(/\x1B\[[0-?]*[ -/]*[@-~]/g, "");
    } catch {
      return value;
    }
  };

  private extractFrontendDomain = (output: string): string | undefined => {
    if (!output) return undefined;
    const text: string = this.stripAnsi(output);
    const match: RegExpMatchArray | null = text.match(/FRONTEND_URL\s*[:=]\s*['"]?([^\s'"]+)/);
    if (!match) return undefined;
    let value: string = match[1].trim();
    value = value.replace(/^https?:\/\//i, "");
    value = value.replace(/\/$/, "");
    return value || undefined;
  };

  handleConsoleTypeChange = (consoleType: "parent" | "backend" | "frontend") => {
    this.setState({ selectedConsoleType: consoleType });
  };

  getBuildStatusText = (result: string): string => {
    switch (result.toUpperCase()) {
      case "SUCCESS":
      case "STABLE":
        return "Ready";
      case "FAILURE":
      case "FAILED":
        return "Failed";
      case "UNSTABLE":
        return "Unstable";
      case "IN_PROGRESS":
      case "BUILDING":
        return "Building";
      default:
        return "Unknown";
    }
  };

  private getStatusVariant = (status: string): "ready" | "failed" | "building" | "unstable" | "unknown" => {
    switch ((status || "").toLowerCase()) {
      case "ready":
        return "ready";
      case "failed":
        return "failed";
      case "building":
        return "building";
      case "unstable":
        return "unstable";
      default:
        return "unknown";
    }
  };

  private getEnvironmentVariant = (env: string): "production" | "staging" | "unknown" => {
    switch ((env || "").toLowerCase()) {
      case "production":
        return "production";
      case "web":
      case "mobile":
      case "wfe":
        return "production";
      case "staging":
      case "preprod":
      case "uat":
      case "test":
        return "staging";
      default:
        return "unknown";
    }
  };

  render() {
    const { appName = "", buildNumber }: { appName?: string; buildNumber?: string } = this.props;
    const {
      loading,
      error,
      buildData,
      selectedConsoleType,
      consoleLogs
    }: {
      loading: boolean;
      error?: string;
      buildData?: IBuildDto;
      selectedConsoleType: "parent" | "backend" | "frontend";
      consoleLogs: DeploymentState["consoleLogs"];
    } = this.state;

    const displayProject: string = buildData?.project || appName;
    const channelFromState =
      buildData?.deploymentChannel || this.props.deploymentRow?.deploymentChannel || this.props.channelFromQuery;
    const displayEnvironment: string = channelFromState
      ? channelFromState.toUpperCase()
      : (this.props.deploymentRow?.environment || "PRODUCTION").toUpperCase();
    const environmentLabel: string = displayEnvironment.charAt(0) + displayEnvironment.slice(1).toLowerCase();
    let displayStatus: string = "Ready";
    if (buildData) {
      displayStatus = this.getBuildStatusText(buildData.result);
    }
    const displayBuildNumber: number | undefined =
      buildData?.number || (buildNumber ? parseInt(buildNumber) : undefined);
    const displayLastDeployment: string | undefined = buildData?.date;
    const lastDeploymentDt: dayjs.Dayjs | undefined = displayLastDeployment ? dayjs(displayLastDeployment) : undefined;
    const lastDeploymentRelative: string | undefined = lastDeploymentDt ? lastDeploymentDt.fromNow() : undefined;
    const lastDeploymentAbsolute: string | undefined = lastDeploymentDt ? lastDeploymentDt.format("ll LT") : undefined;
    const deploymentDisplayName: string = `${displayProject} ${environmentLabel}`.trim();

    const currentConsoleLog: DeploymentState["consoleLogs"]["parent"] = consoleLogs[selectedConsoleType];

    if (loading) {
      return (
        <div className="kuika-settings-panel deployment">
          <div className="kuika-settings-panel__header">
            <div className="kuika-settings-panel__header-left">
              <KuikaSvg width={24} height={24} name="rocket" />
              <KuikaH4>
                Deployments / {displayProject} / #{displayBuildNumber}
              </KuikaH4>
            </div>
          </div>
          <div className="kuika-settings-panel__content deployment__content">
            <div style={{ padding: "16px", textAlign: "center" }}>Loading deployment details...</div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="kuika-settings-panel deployment">
          <div className="kuika-settings-panel__header">
            <div className="kuika-settings-panel__header-left">
              <KuikaSvg width={24} height={24} name="rocket" />
              <KuikaH4>
                Deployments / {displayProject} / #{displayBuildNumber}
              </KuikaH4>
            </div>
          </div>
          <div className="kuika-settings-panel__content deployment__content">
            <div style={{ padding: "16px", color: "var(--red-6)" }}>Error: {error}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="kuika-settings-panel deployment">
        <div className="kuika-settings-panel__header">
          <div className="kuika-settings-panel__header-left">
            <KuikaSvg width={24} height={24} name="rocket" />
            <KuikaH4>
              Deployments / {displayProject} / #{displayBuildNumber}
            </KuikaH4>
          </div>
        </div>

        <div className="kuika-settings-panel__content deployment__content">
          <div className="deployment__content-card" role="region" aria-label="Deployment details">
            <div className="deployment__summary">
              <KuikaTextMedium className="deployment__summary-name" aria-label="Deployment name">
                {deploymentDisplayName || "Deployment"}
              </KuikaTextMedium>
              <div className="deployment__metric" aria-label="Function CPU">
                <KuikaText className="deployment__metric-title">Configuration</KuikaText>
                <div className="deployment__metric-bottom">
                  <KuikaSmallText className="deployment__metric-text-left">0.6 vCPU</KuikaSmallText>
                  <KuikaSmallText className="deployment__metric-text-right">1 GB Memory</KuikaSmallText>
                </div>
              </div>
            </div>

            <div className="deployment__info" role="group" aria-label="Deployment information">
              <div className="deployment__info-field">
                <KuikaSmallBoldText className="deployment__info-label">Domain</KuikaSmallBoldText>
                <KuikaTextMedium className="deployment__info-value">
                  {(() => {
                    const domainToDisplay: string = this.state.domain || "www.kuika.com";
                    const href: string = `https://${domainToDisplay}`;
                    return (
                      <a
                        className="deployment__domain-link"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${domainToDisplay} in a new tab`}
                      >
                        <span className="deployment__domain-link-text">{domainToDisplay}</span>
                        <KuikaSvg
                          name="external-link"
                          width={14}
                          height={14}
                          color="currentColor"
                          className="deployment__domain-link-icon"
                        />
                      </a>
                    );
                  })()}
                </KuikaTextMedium>
              </div>
              <div className="deployment__info-field">
                <KuikaSmallBoldText className="deployment__info-label">Status</KuikaSmallBoldText>
                {((): React.ReactNode => {
                  const variant: ReturnType<typeof this.getStatusVariant> = this.getStatusVariant(displayStatus);
                  return (
                    <div className="deployment__status" aria-label={`Deployment status: ${displayStatus}`}>
                      <div className={`deployment__status-badge deployment__status-badge--${variant}`}>
                        <span className="deployment__status-dot" aria-hidden="true" />
                        <span className="deployment__status-label">{displayStatus}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="deployment__info-field">
                <KuikaSmallBoldText className="deployment__info-label">Last Deployment</KuikaSmallBoldText>
                <div className="deployment__date" aria-label="Last deployment time">
                  <KuikaTooltip content={lastDeploymentAbsolute} position="top">
                    <span className="deployment__date-badge" title={lastDeploymentAbsolute || undefined}>
                      <KuikaSvg name="clock-hour-10" width={14} height={14} />
                      <span className="deployment__date-label">{lastDeploymentRelative || "N/A"}</span>
                    </span>
                  </KuikaTooltip>
                  {lastDeploymentAbsolute && (
                    <KuikaSmallText className="deployment__date-absolute">{lastDeploymentAbsolute}</KuikaSmallText>
                  )}
                </div>
              </div>
              <div className="deployment__info-field">
                <KuikaSmallBoldText className="deployment__info-label">Environment</KuikaSmallBoldText>
                {((): React.ReactNode => {
                  const envVariant: ReturnType<typeof this.getEnvironmentVariant> =
                    this.getEnvironmentVariant(displayEnvironment);
                  return (
                    <div className="deployment__environment" aria-label={`Environment: ${environmentLabel}`}>
                      <span className={`deployment__env-tag deployment__env-tag--${envVariant}`}>
                        <KuikaSvg name="world-www" width={14} height={14} />
                        <span className="deployment__env-tag-label">{environmentLabel}</span>
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Console Output Section */}
          <div className="deployment__console-section" style={{ marginTop: "24px" }}>
            <div className="deployment__console-header">
              <KuikaTextMedium>Console Output</KuikaTextMedium>
              {(() => {
                const tabs: Array<{
                  key: "parent" | "backend" | "frontend";
                  label: string;
                  icon: React.ReactNode;
                }> = [
                  {
                    key: "parent",
                    label: "Parent",
                    icon: <KuikaSvg name="code" width={14} height={14} color="currentColor" />
                  },
                  {
                    key: "backend",
                    label: "Backend",
                    icon: <KuikaSvg name="api" width={14} height={14} color="currentColor" />
                  },
                  {
                    key: "frontend",
                    label: "Frontend",
                    icon: <KuikaSvg name="app" width={14} height={14} color="currentColor" />
                  }
                ];

                const handleKeyDown = (
                  e: React.KeyboardEvent<HTMLDivElement | HTMLButtonElement>,
                  currentIndex: number
                ) => {
                  const keys: string[] = ["ArrowLeft", "ArrowRight", "Home", "End"];
                  if (!keys.includes(e.key)) return;
                  e.preventDefault();

                  let nextIndex: number = currentIndex;
                  if (e.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                  if (e.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
                  if (e.key === "Home") nextIndex = 0;
                  if (e.key === "End") nextIndex = tabs.length - 1;

                  const next: { key: "parent" | "backend" | "frontend"; label: string; icon: React.ReactNode } =
                    tabs[nextIndex];
                  this.handleConsoleTypeChange(next.key);
                  // Move focus to the newly selected tab
                  const id: string = `deployment-console-tab-${next.key}`;
                  const el: HTMLElement | null = document.getElementById(id);
                  el?.focus();
                };

                return (
                  <div
                    className="deployment__console-tabs"
                    role="tablist"
                    aria-label="Console tabs"
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      // Delegate left/right on the container to currently selected tab index
                      const idx: number = tabs.findIndex((t) => t.key === selectedConsoleType);
                      handleKeyDown(e, Math.max(0, idx));
                    }}
                  >
                    {tabs.map(
                      (
                        t: { key: "parent" | "backend" | "frontend"; label: string; icon: React.ReactNode },
                        index: number
                      ) => {
                        const isActive: boolean = selectedConsoleType === t.key;
                        const isLoading: boolean = this.state.consoleLogs[t.key].loading;
                        return (
                          <button
                            key={t.key}
                            id={`deployment-console-tab-${t.key}`}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls="deployment-console-output"
                            tabIndex={isActive ? 0 : -1}
                            title={`${t.label} console`}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            onClick={() => this.handleConsoleTypeChange(t.key)}
                            disabled={isLoading}
                            className={`deployment__console-tab ${isActive ? "deployment__console-tab--active" : ""} ${
                              isLoading ? "deployment__console-tab--loading" : ""
                            }`}
                          >
                            <span className="deployment__console-tab-icon" aria-hidden="true">
                              {t.icon}
                            </span>
                            <span className="deployment__console-tab-label">{t.label}</span>
                          </button>
                        );
                      }
                    )}
                  </div>
                );
              })()}
            </div>

            <div
              id="deployment-console-output"
              role="tabpanel"
              aria-labelledby={`deployment-console-tab-${selectedConsoleType}`}
              className="deployment__console-output"
            >
              {/* Only render active console - pre-rendered HTML is cached in state */}
              {currentConsoleLog.loading && <div style={{ color: "#58a6ff" }}>⠿ Loading console output...</div>}
              {currentConsoleLog.error && <div style={{ color: "#f85149" }}>✗ Error: {currentConsoleLog.error}</div>}
              {!currentConsoleLog.loading && !currentConsoleLog.error && currentConsoleLog.renderedHtml && (
                <div
                  className="deployment__console-pane"
                  dangerouslySetInnerHTML={{ __html: currentConsoleLog.renderedHtml }}
                />
              )}
              {!currentConsoleLog.loading && !currentConsoleLog.error && !currentConsoleLog.renderedHtml && (
                <div style={{ color: "#8b949e" }}>$ No console output available</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const DeploymentWithParams = (props: IWithHistoryProp) => {
  const { appName, buildNumber } = useParams<{ appName: string; buildNumber: string }>();
  const location = useLocation();
  const deploymentRow = (location.state as any)?.deploymentRow as DeploymentRow | undefined;
  const searchParams = new URLSearchParams(location.search || "");
  const channelFromQuery = searchParams.get("channel") ?? undefined;
  return (
    <DeploymentBase
      {...props}
      appName={appName}
      buildNumber={buildNumber}
      deploymentRow={deploymentRow}
      channelFromQuery={channelFromQuery}
    />
  );
};

const Deployment = withManagement(withHistory(DeploymentWithParams));
export { Deployment };
